import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Sparkles } from "lucide-react";
import { Input } from "../ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "rounded-lg border-white/10 bg-[#0b0c0f] text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0";
export const OFF_ROAD_NOTE = "Off-road / motorsport use only";
export const extraLine = (x) => ({ description: x.note ? `${x.name} (${x.note})` : x.name, amount: x.price });

const errMsg = (err, fallback) => { const d = err?.response?.data?.detail; return typeof d === "string" ? d : fallback; };

const ExtraFields = ({ v, set, i }) => (
  <>
    <Input value={v.name} maxLength={60} placeholder="e.g. Pops & bangs" onChange={(e) => set({ ...v, name: e.target.value })} className={`${field} h-10 min-w-[140px] flex-1`} data-testid={`extra-name-${i}`} />
    <div className="relative w-28">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/50">£</span>
      <Input type="number" step="0.01" value={v.price} onChange={(e) => set({ ...v, price: e.target.value })} className={`${field} h-10 pl-7`} data-testid={`extra-price-${i}`} />
    </div>
    <Input value={v.note} maxLength={50} list="extra-notes" placeholder="Note on quote (optional)" onChange={(e) => set({ ...v, note: e.target.value })} className={`${field} h-10 min-w-[160px] flex-1`} data-testid={`extra-note-${i}`} />
  </>
);

const ExtraRow = ({ x, auth, onChanged }) => {
  const [v, setV] = useState(x);
  const dirty = v.name !== x.name || Number(v.price) !== Number(x.price) || v.note !== x.note;
  const save = async () => {
    try { await axios.put(`${API}/admin/extras/${x.id}`, { ...v, price: Number(v.price) }, auth); toast.success("Extra saved"); onChanged(); }
    catch (err) { toast.error(errMsg(err, "Please enter a name and price")); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove "${x.name}" from your saved extras?`)) return;
    try { await axios.delete(`${API}/admin/extras/${x.id}`, auth); toast.success(`"${x.name}" removed`); onChanged(); }
    catch { toast.error("Couldn't remove extra"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`extra-row-${x.id}`}>
      <ExtraFields v={v} set={setV} i={x.id} />
      <button disabled={!dirty || !v.name.trim() || v.price === ""} onClick={save} className="flex h-10 items-center gap-1 rounded-lg border border-white/15 px-3 text-sm text-white/80 hover:border-white/40 disabled:opacity-30" data-testid={`extra-save-${x.id}`}><Save className="h-4 w-4" /></button>
      <button onClick={remove} className="flex h-10 items-center px-2 text-white/40 hover:text-brand" data-testid={`extra-delete-${x.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
};

const AdminExtras = ({ token }) => {
  const [items, setItems] = useState([]);
  const [nv, setNv] = useState({ name: "", price: "", note: "" });
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => axios.get(`${API}/admin/extras`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setItems(data)).catch(() => toast.error("Couldn't load extras")), [token]);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/admin/extras`, { ...nv, price: Number(nv.price) }, auth); toast.success(`"${nv.name}" added`); setNv({ name: "", price: "", note: "" }); load(); }
    catch (err) { toast.error(errMsg(err, "Please enter a name and price")); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-[#111217] p-6" data-testid="extras-editor">
      <h2 className="flex items-center gap-2 font-display text-3xl font-bold uppercase text-white"><Sparkles className="h-6 w-6 text-brand" />Quote extras</h2>
      <p className="mt-1 text-sm text-white/50">Saved add-ons you can tap into any quote. Prices are a starting point, you can still change them on each quote.</p>
      <datalist id="extra-notes"><option value={OFF_ROAD_NOTE} /></datalist>
      <div className="mt-5 space-y-2" data-testid="extras-list">
        {items.length === 0 && <div className="rounded-lg border border-dashed border-white/10 p-5 text-center text-sm text-white/40">No extras saved yet.</div>}
        {items.map((x) => <ExtraRow key={`${x.id}-${x.updated_at || ""}`} x={x} auth={auth} onChanged={load} />)}
      </div>
      <form onSubmit={add} className="mt-5 flex flex-wrap items-center gap-2 border-t border-white/10 pt-5" data-testid="extra-add-form">
        <ExtraFields v={nv} set={setNv} i="new" />
        <button disabled={!nv.name.trim() || nv.price === ""} className="btn-red flex h-10 items-center gap-1.5 rounded-lg px-4 text-sm font-semibold disabled:opacity-50" data-testid="extra-add"><Plus className="h-4 w-4" />Add extra</button>
      </form>
    </div>
  );
};

export default AdminExtras;
